import { Info, OctagonX, RefreshCw, Search, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { api } from "../api";
import {
  ActionButton,
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  Panel,
  SectionHeader,
  Tag,
} from "../components/design-system";
import { useResource } from "../hooks/use-resource";
import type { AuditEvent, AuditLevel } from "../types";
import { actionLabel, formatDateTime } from "../utils";

type LevelFilter = "all" | AuditLevel;

const PAGE_SIZE = 8;
const LEVEL_META = {
  info: { icon: Info, label: "信息", className: "text-sage" },
  warn: { icon: TriangleAlert, label: "警告", className: "text-clay-deep" },
  danger: { icon: OctagonX, label: "危险", className: "text-destructive" },
} as const;

export function AuditPage() {
  const [level, setLevel] = useState<LevelFilter>("all");
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [draftQuery, setDraftQuery] = useState("");
  const audit = useResource(
    () =>
      api.audit({
        page,
        pageSize: PAGE_SIZE,
        q: query,
        level: level === "all" ? undefined : level,
      }),
    [page, query, level],
  );
  const pages = audit.data?.pagination.totalPages || 0;
  const facets = audit.data?.facets || { all: 0, info: 0, warn: 0, danger: 0 };

  const filters: Array<{ key: LevelFilter; label: string }> = [
    { key: "all", label: "全部" },
    { key: "info", label: "信息" },
    { key: "warn", label: "警告" },
    { key: "danger", label: "危险" },
  ];

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="TRAIL"
        title="操作审计"
        desc={
          audit.data
            ? `共 ${audit.data.pagination.total} 条匹配记录 · 第 ${audit.data.pagination.page} / ${Math.max(1, pages)} 页`
            : "登录与关键管理操作均会留下记录"
        }
        className="rise-in"
        action={
          <ActionButton
            busy={audit.refreshing}
            onClick={() => void audit.refresh()}
          >
            <RefreshCw size={13} />
            刷新日志
          </ActionButton>
        }
      />

      <div className="rise-in rise-in-1 flex flex-wrap items-center gap-2">
        {filters.map((filter) => (
          <button
            key={filter.key}
            onClick={() => {
              setLevel(filter.key);
              setPage(1);
            }}
            className={`rounded-full border px-4 py-1.5 text-[12.5px] font-medium transition active:scale-95 ${level === filter.key ? "border-ink bg-ink text-paper" : "border-sand-light bg-paper text-ink-soft hover:border-sand"}`}
          >
            {filter.label}
            <span className="ml-1.5 text-[10.5px] opacity-60">
              {facets[filter.key]}
            </span>
          </button>
        ))}
        <form
          className="ml-auto flex min-w-52 items-center gap-2 rounded-full border border-sand-light bg-paper px-4 py-1.5 text-[12.5px]"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setQuery(draftQuery.trim());
          }}
        >
          <Search size={13} className="text-ink-faint" />
          <input
            value={draftQuery}
            onChange={(event) => setDraftQuery(event.target.value)}
            placeholder="搜索动作或目标…"
            className="w-44 bg-transparent outline-none placeholder:text-ink-faint/70"
          />
        </form>
      </div>

      {audit.error && (
        <ErrorState error={audit.error} onRetry={() => void audit.refresh()} />
      )}
      <Panel className="rise-in rise-in-2 p-2 sm:p-4">
        {audit.loading && !audit.data ? (
          <LoadingState rows={8} />
        ) : audit.data?.items.length ? (
          <>
            <ol className="relative ml-3 border-l border-sand sm:ml-5">
              {audit.data.items.map((event, index) => (
                <AuditItem key={event.id} event={event} index={index} />
              ))}
            </ol>
            <Pagination
              page={audit.data.pagination.page}
              pages={audit.data.pagination.totalPages}
              total={audit.data.pagination.total}
              onChange={setPage}
              className="border-t border-sand-light/70 px-4 py-4"
            />
          </>
        ) : (
          <EmptyState
            title="没有审计记录"
            description="调整级别或搜索条件，完成管理操作后对应事件会显示在这里。"
          />
        )}
      </Panel>
    </div>
  );
}

function AuditItem({ event, index }: { event: AuditEvent; index: number }) {
  const meta = LEVEL_META[event.level];
  const LevelIcon = meta.icon;
  return (
    <li className="group relative pb-1 pl-7 pt-4 last:pb-4 sm:pl-9">
      <span
        className={`absolute -left-[11px] top-5 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-sand-light bg-paper transition group-hover:border-sand ${meta.className}`}
      >
        <LevelIcon size={12} strokeWidth={2} />
      </span>
      <div
        className="rise-in rounded-xl px-4 py-3 transition group-hover:bg-cream/70"
        style={{ animationDelay: `${Math.min(index * 40, 300)}ms` }}
      >
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <p className="text-[13.5px] font-medium text-ink">
            {event.actor}
            <span className="mx-1.5 text-ink-faint">·</span>
            {actionLabel(event.action)}
          </p>
          <p className="ml-auto font-mono text-[11px] text-ink-faint">
            {formatDateTime(event.createdAt)}
          </p>
        </div>
        <p className="mt-1 text-[12.5px] text-ink-soft">
          {event.targetType && event.targetId
            ? `${targetLabel(event.targetType)} · ${event.targetId}`
            : "管理后台"}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-ink-faint">
          <Tag
            tone={
              event.level === "danger"
                ? "danger"
                : event.level === "warn"
                  ? "clay"
                  : "sage"
            }
          >
            {meta.label}
          </Tag>
          <span>来源 IP {event.ipAddress || "未知"}</span>
        </div>
        {event.details && (
          <details className="mt-2 rounded-lg bg-cream/70 px-3 py-2">
            <summary className="cursor-pointer text-[11.5px] text-ink-soft">
              查看操作详情
            </summary>
            <pre className="scrollbar-thin mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-all font-mono text-[10.5px] leading-relaxed text-ink-soft">
              {JSON.stringify(event.details, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </li>
  );
}

function targetLabel(target: string) {
  return (
    (
      {
        user: "用户",
        snapshot: "持久快照",
        job: "定时任务",
        publication: "公告 / 通知",
        publication_media: "公告图片",
      } as Record<string, string>
    )[target] || target
  );
}
